import type { Fiber } from 'react-reconciler'

function detectPropagation(op?: boolean | AddEventListenerOptions) {
  if (typeof op === 'undefined') return true
  if (typeof op === 'boolean') return !op

  return op?.capture !== false
}

function getDomIndex(target: HTMLElement) {
  if (!target.parentNode) return -1
  for (let t = 0, r = target.tagName, n = target.parentNode.children, i = 0; i < n.length; i++)
    if (n[i].tagName === r) {
      if (target === n[i]) return t
      t++
    }

  return -1
}

function getPrefix() {
  const prefix = window.location.pathname.split('/').filter(Boolean)
  const project = prefix.shift()!.toUpperCase()
  const res = [project]
  prefix.forEach((item) =>
    res.push(
      item
        .replace(/^[a-zA-Z]/, (m) => m.toUpperCase())
        .replace(/_([a-zA-Z])/g, (_m, $1) => $1.toUpperCase())
    )
  )

  return res.filter(Boolean).join('@')
}

function formatText(text?: string | null, maxLength = 30) {
  if (!text) return undefined
  // 压缩空格
  text = text.trim().replace(/\s+/g, ' ')
  if (text.length <= maxLength) return text

  const left = Math.ceil((maxLength / 10) * 6)

  return `${text.slice(0, left)}...${text.slice(-left)}`
}

function isDomNode(ele: ParentNode): ele is HTMLElement {
  return ele && ele.nodeType === 1 && typeof (ele as HTMLElement).tagName === 'string'
}

function getNodeContent(node?: HTMLElement | null) {
  if (!node) return null
  let c = node.innerText || node.getAttribute('title') || node.getAttribute('alt')

  if (!c) {
    if (node.getAttribute('href')) {
      c = `${node.tagName.toLowerCase()}:${node.getAttribute('href')}`
    } else if (node.getAttribute('src')) {
      c = `${node.tagName.toLowerCase()}:${node.getAttribute('src')}`
    }
  }

  if (!c || !c.trim()) return getNodeContent(node?.parentElement);

  return c.trim()
}

/**
 * @description 从ReactFiber 获取信息
 * @example
 * @ 表示 域分割
 * -- 表示 属性分割
 * _ 表示 属性描述
 * : 表示 属性修饰
 */
function genReactFiberInfo($reactFiber: Fiber, event: MouseEvent) {
  const root: HTMLElement = $reactFiber.stateNode
  let target: ParentNode | null = root

  const inner = {
    $title: document.title,
    $url_path: window.location.pathname,
    $element_type: root.tagName ? root.tagName.toLocaleLowerCase() : 'unknown',
    $element_class_name: root.className || '',
    $element_content: root.innerText || '',
    $page_x: event.x,
    $page_y: event.y
  }

  const $path = []
  const $selector = []
  const prefix = getPrefix()
  const prefixZH = "整车权益"
  const button_name = formatText(
    getNodeContent(root) || getNodeContent(event.target as HTMLElement)
  )

  const content = [formatText(document.title), button_name]

  while (target) {
    if (!isDomNode(target)) break

    const tagName = target.tagName.toLocaleLowerCase()
    if (/^[A-Za-z][-A-Za-z0-9_:.]*$/.test(target.id || '')) {
      $path.push(`${tagName}#${target.id}`)
      $selector.push(`${tagName}#${target.id}`)
      break
    }

    if (target === document.body) {
      $path.push('body')
      $selector.push('body')
      break
    }

    $path.push(tagName)
    const index = getDomIndex(target)
    $selector.push(`${tagName}${index > -1 ? `:nth-of-type(${index + 1})` : ''}`)

    target = target.parentNode
  }

  $path.reverse()
  $selector.reverse()
  let fnName =
    $reactFiber.memoizedProps && typeof $reactFiber.memoizedProps.onClick == 'function'
      ? $reactFiber.memoizedProps.onClick.name || 'unknown'
      : 'unknown'

  if (fnName === 'onClick' || fnName === 'unknown') fnName = undefined

  return {
    root,
    inner: {
      ...inner,
      $element_path: $path.join(' > '),
      $element_selector: $selector.join(' > ')
    },
    attributes: {
      'evtId': `${prefix}--${$selector
        .join('-')
        .replace(/:nth-of-type\((\d+)\)/g, '$1')
        .replace(/-([A-Za-z]+)/g, (_m, $1) => `-${$1[0].toUpperCase()}`)}`,
      'evtName': `${prefixZH}@${content.filter(Boolean).join('--')}${
        fnName ? `:${fnName}` : ''
      }`,
      button_name,
      prefix,
      prefixZH: prefixZH
    }
  }
}

type ReactFiberFilterOption = {
  excludeClassNames?: string[]
  excludeAttributes?: string[]
  filter?: (target: HTMLElement) => boolean
}

function findReactFiber(event: MouseEvent, option: ReactFiberFilterOption = {}) {
  if (!event || !event.target) return null
  let target = event.target as HTMLElement
  /**
   * @see https://github.com/facebook/react/blob/main/packages/react-dom-bindings/src/client/ReactDOMComponentTree.js#L40
   * const internalInstanceKey = '__reactFiber$' + randomKey;
   * const internalPropsKey = '__reactProps$' + randomKey;
   * const internalContainerInstanceKey = '__reactContainer$' + randomKey;
   * const internalEventHandlersKey = '__reactEvents$' + randomKey;
   * const internalEventHandlerListenersKey = '__reactListeners$' + randomKey;
   * const internalEventHandlesSetKey = '__reactHandles$' + randomKey;
   * const internalRootNodeResourcesKey = '__reactResources$' + randomKey;
   * const internalHoistableMarker = '__reactMarker$' + randomKey;
   */
  // const key = Object.keys(target).find(
  //   (k) => k.indexOf('__reactFiber$') > -1
  // ) as keyof typeof target

  const getKey = (ele: HTMLElement | null): keyof HTMLElement | null => {
    if (!ele) return null;

    const fiberKey = Object.keys(ele).find((k) => k.indexOf("__reactFiber$") > -1) as keyof HTMLElement;

    return fiberKey ? fiberKey : getKey(ele?.parentElement ? (target = ele.parentElement) : null);
  };

  const key = getKey(target);

  if (!key) return null
  const $reactFiber = target[key] as unknown as Fiber

  let root: Fiber | null = $reactFiber
  while (root) {
    /**
     * @see https://github.com/facebook/react/blob/main/packages/react-reconciler/src/ReactWorkTags.js#L32
     * const HostComponent = 5; ( 5 是原生标签)
     */
    if (!root.elementType || root.tag !== 5) {
      root = root.return
      continue
    }

    const dom: HTMLElement = root.stateNode
    if (typeof option.filter === 'function') {
      if (!option.filter(dom)) {
        return null
      }
    }

    for (const cls of option.excludeClassNames || []) {
      if (dom.classList.contains(cls)) {
        return null
      }
    }

    for (const attr of option.excludeAttributes || []) {
      if (dom.hasAttribute(attr)) {
        return null
      }
    }

    if (root.memoizedProps && typeof root.memoizedProps.onClick === 'function') {
      return genReactFiberInfo(root, event)
    }

    root = root.return
  }

  return null
}

export type AttachEventListenersOption<R> = ReactFiberFilterOption & {
  format?: (data: ReturnType<typeof genReactFiberInfo>) => R,
  capture: boolean,
}

function attachSingleEventListeners<T extends object>(
  s: string,
  option: AttachEventListenersOption<T>
) {
  const root = document.querySelector(s)
  if (!root) return
  const addEventListener = root.addEventListener
  root.addEventListener = function _(
    type: string,
    listener: (this: Element, ev: Event) => any,
    options?: boolean | AddEventListenerOptions
  ) {
    let _listener = listener
    /** 只挟持 onClick && 冒泡事件 */
    if (type === 'click' && detectPropagation(options)) {
      _listener = new Proxy(listener, {
        apply(target, _this, args) {
          // 仅 绑定事件元素的 === 劫持root
          if (_this === root) {
            const res = findReactFiber(args[0], option)
            if (res && window.sensorsDataAnalytic201505) {
              const attrs =
                typeof option.format === 'function'
                  ? option.format(res)
                  : Object.assign(
                      {},
                      {
                        element_dataset: JSON.stringify(res.attributes)
                      }
                    )
              window.sensorsDataAnalytic201505.quick('trackHeatMap', res.root, attrs)
            }

            console.log(res)
          }

          return Reflect.apply(target, _this, args)
        }
      })
    }

    addEventListener.call(root, type, _listener, options)
  }
}

/**
 * @param s 选择器 - 默认选择器 [body, #root ]
 * 
 * ====================== 可交互之前的过滤 start ======================
 * @param option.excludeAttributes?: string[] （需要排除的属性）例如 [data-sensors-click]
 * @param option.excludeClassNames?: string[] (需要排除的类名)
 * @param option.filter?: (target: EventTarget) => boolean (false 表示 直接结束)
 * ====================== 可交互之前的过滤 end ======================
 * 
 * @param option.format?: ({
      root: HTMLElement,
      inner: {
        $element_class_name: "",
        //@example div#root > div > div > div > div > div > section > section > div
        $element_path: $path.join(' > '),
        //@example div#root > div:nth-of-type(1) > div:nth-of-type(1) > div:nth-of-type(3) > div:nth-of-type(1)
        $element_selector: $selector.join(' > '),
        ...,
      },
      // @example  @ 表示 域分割 -- 表示 属性分割 _ 表示 属性描述 : 表示 属性修饰
      attributes: {
        'data-evt-id': "WISH--Carset@div#root-D1-D1-D3-D1-D2-S1-S1-D1",
        'data-evt-name': "WISH--Carset@定制我的专属阿维塔--90度 后驱版 限时特惠 ¥400,... 百公里加速3.98s 更多 已下架:unknown",
        // 压缩空格 （20字左右）
        button_name: "90度 后驱版 限时特惠 ¥400,... 百公里加速3.98s 更多 已下架",
        // 项目(全大写)-pathname(首字母大写)
        prefix: "WISH--Carset",
      },
    }) => object (上报的的数据)
 */
export function attachEventListeners<T extends object>(
  s: string | string[] = ['body', '#root'],
  option: AttachEventListenersOption<T> = {
    excludeAttributes: ['data-sensors-click'],
    capture: true,
  }
) {
  s = Array.isArray(s) ? s : [s]
  s = s.filter(Boolean)
  for (const _s of s) {
    attachSingleEventListeners(_s, option)
  }
}
